/**
 * LlmServiceModule — wraps vibe-designer's POST /api/chat per planner §M3.
 *
 * Per-tenant LLM Router proxy. Returns the assistant completion string.
 * Bearer JWT from localStorage (same key as vibe-designer's api.ts).
 *
 * Failure modes: HTTP non-2xx + network errors throw; the caller
 * decides whether to surface a degradation banner or retry. isAvailable
 * is always true in module-host mode (the route is always mounted);
 * the standalone variant overrides this to false.
 */
import type { ChatMessage, LlmService } from '../types';
import { getAuthHeaders } from './authHeaders';

const CHAT_PATH = '/api/chat';

interface VibeChatResponse {
  content?: unknown;
  completion?: unknown;
  message?: { content?: unknown };
  choices?: Array<{ message?: { content?: unknown } }>;
}

function extractAssistantText(body: VibeChatResponse): string {
  if (typeof body.content === 'string') {
    return body.content;
  }
  if (typeof body.completion === 'string') {
    return body.completion;
  }
  if (body.message !== undefined && typeof body.message.content === 'string') {
    return body.message.content;
  }
  const first = body.choices?.[0];
  if (first !== undefined && typeof first.message?.content === 'string') {
    return first.message.content;
  }
  return '';
}

export class LlmServiceModule implements LlmService {
  constructor(
    private readonly chatPath: string = CHAT_PATH,
    // See PersistenceServiceModule for the "Illegal invocation" rationale.
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  isAvailable(): boolean {
    return true;
  }

  async chat(messages: ChatMessage[]): Promise<string> {
    const resp = await this.fetchFn(this.chatPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ messages, stream: false }),
    });
    if (!resp.ok) {
      throw new Error(`LlmServiceModule: chat returned HTTP ${resp.status}`);
    }
    const body = (await resp.json()) as VibeChatResponse;
    return extractAssistantText(body);
  }
}
